import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, X, ChevronRight, Sparkles, Droplet, Beef, UtensilsCrossed, Flame,
  TrendingUp, MoonStar, Loader2,
} from "lucide-react";
import api from "@/lib/api";

// localStorage key — we persist per-user dismissed reminder ids so a banner
// the user has closed today doesn't return until tomorrow.
const dismissKey = (userId) => `gu_dismissed_reminders_${userId || "anon"}`;

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function readDismissed(userId) {
  try {
    const raw = localStorage.getItem(dismissKey(userId));
    if (!raw) return { day: todayKey(), ids: [] };
    const parsed = JSON.parse(raw);
    // Prune stale data — only the current day's dismissals are relevant.
    if (parsed?.day !== todayKey()) {
      localStorage.removeItem(dismissKey(userId));
      return { day: todayKey(), ids: [] };
    }
    return parsed;
  } catch { return { day: todayKey(), ids: [] }; }
}

function writeDismissed(userId, ids) {
  try {
    localStorage.setItem(
      dismissKey(userId),
      JSON.stringify({ day: todayKey(), ids }),
    );
  } catch { /* ignore */ }
}

function iconFor(category) {
  switch (category) {
    case "water":          return Droplet;
    case "protein":        return Beef;
    case "meal_log":       return UtensilsCrossed;
    case "calorie_goal":   return Flame;
    case "daily_summary":  return TrendingUp;
    case "motivational":   return Sparkles;
    case "streak":         return Sparkles;
    case "congratulations":return Sparkles;
    default:               return Bell;
  }
}

/**
 * Smart in-app reminder banner. Polls /api/reminders/today and shows the
 * most urgent un-dismissed reminder. Persists dismissals to localStorage
 * for the current day so the same nudge doesn't reappear after refresh.
 */
export default function RemindersBanner({ userId }) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dismissed, setDismissed] = useState(() => readDismissed(userId).ids);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reminders/today");
      setReminders(Array.isArray(data?.reminders) ? data.reminders : []);
    } catch {
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + a 5-minute refresh so reminders pick up time-of-day
  // transitions without a manual page refresh.
  useEffect(() => {
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  // Re-read dismissed map when the user id changes (e.g. login → switch).
  useEffect(() => {
    setDismissed(readDismissed(userId).ids);
  }, [userId]);

  const visible = reminders.filter((r) => !dismissed.includes(r.id));
  if (loading || visible.length === 0) return null;

  const r = visible[0];
  const Icon = iconFor(r.category);
  const isCelebrate = r.severity === "celebrate";
  const isWarn      = r.severity === "warn";

  const dismiss = () => {
    const next = [...dismissed, r.id];
    setDismissed(next);
    writeDismissed(userId, next);
  };

  const act = () => {
    if (r.action_route) navigate(r.action_route);
    dismiss();
  };

  return (
    <div
      className="card"
      data-testid="reminders-banner"
      data-reminder-id={r.id}
      data-reminder-category={r.category}
      style={{
        padding: 14, marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12,
        border: "1px solid " + (
          isCelebrate ? "rgba(232,211,185,0.40)" :
          isWarn      ? "rgba(255,160,120,0.40)" :
                        "rgba(255,255,255,0.10)"
        ),
        background: (
          isCelebrate ? "linear-gradient(135deg, rgba(232,211,185,0.10), rgba(232,211,185,0.02))" :
          isWarn      ? "rgba(255,160,120,0.04)" :
                        "var(--surface-soft)"
        ),
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 36, height: 36, flexShrink: 0, borderRadius: 10,
          background: isCelebrate ? "var(--gold)" : "rgba(255,255,255,0.04)",
          color:      isCelebrate ? "#000" : (isWarn ? "#ffb088" : "var(--gold)"),
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14, fontWeight: 600, marginBottom: 4,
            color: isCelebrate ? "var(--gold)" : "var(--text)",
          }}
          data-testid="reminder-title"
        >
          {r.title}
        </div>
        <div
          className="tiny"
          style={{ color: "var(--text-dim)", lineHeight: 1.5 }}
          data-testid="reminder-body"
        >
          {r.body}
        </div>
        {r.action_label && (
          <button
            type="button"
            onClick={act}
            data-testid="reminder-action"
            className="kbd-pill"
            style={{
              marginTop: 10, cursor: "pointer", background: "transparent",
              color: "var(--gold)", borderColor: "rgba(232,211,185,0.40)",
              padding: "5px 10px", display: "inline-flex", gap: 4,
              alignItems: "center", fontSize: 12,
            }}
          >
            {r.action_label} <ChevronRight size={12} strokeWidth={2} />
          </button>
        )}
      </div>
      {r.dismissible !== false && (
        <button
          type="button"
          onClick={dismiss}
          data-testid="reminder-dismiss"
          aria-label="Dismiss reminder"
          className="icon-btn"
          style={{ width: 28, height: 28, flexShrink: 0 }}
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      )}
    </div>
  );
}

/** Public hook — useful when the Home screen wants to show its own variation
 *  of the banner (e.g. surface the second reminder in a sidebar). */
export function useTodayReminders() {
  const [list, setList] = useState([]);
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const { data } = await api.get("/reminders/today");
        if (!aborted) setList(data?.reminders || []);
      } catch {
        if (!aborted) setList([]);
      }
    })();
    return () => { aborted = true; };
  }, []);
  return list;
}

// Re-export Loader2 + MoonStar so other consumers can grab matching icons.
export { Loader2 as ReminderSpinner, MoonStar };
